import { Box } from "@mui/material";

import { currencyFormatter, formatChannelLabel } from "../../../lib/presentation";
import type { ClientTraderDefectSummary } from "../../../lib/api/types";
import type { ProcessedHistoryOrder } from "./types";
import { formatDate } from "./utils";
import HistoryMetricCard from "./HistoryMetricCard";

interface MetricsGridProps {
    firstOrder: ProcessedHistoryOrder | null;
    totalOrders: number;
    latestOrder: ProcessedHistoryOrder | null;
    totalValue: number;
    averageMonthlyFrequency: number;
    averageInterval: number | null;
    traderDefect: ClientTraderDefectSummary;
}

export default function MetricsGrid({
    firstOrder,
    totalOrders,
    latestOrder,
    totalValue,
    averageMonthlyFrequency,
    averageInterval,
    traderDefect,
}: MetricsGridProps) {
    const defectHelper = traderDefect.available
        ? `${traderDefect.defectArticles} от ${traderDefect.purchasedArticles} артикула · ${currencyFormatter.format(traderDefect.defectValue)} от ${currencyFormatter.format(traderDefect.purchasedValue)}`
        : "";

    const metrics = [
        {
            label: "Първа заявка",
            value: firstOrder ? formatDate(firstOrder.createdAt) : "Няма история",
            helper: firstOrder ? `${firstOrder.orderNumber} · ${formatChannelLabel(firstOrder.channel)}` : "Няма видими документи",
        },
        {
            label: "Общ брой заявки",
            value: `${totalOrders}`,
            helper: latestOrder ? `Последна: ${formatDate(latestOrder.createdAt)}` : "Няма последна заявка",
        },
        {
            label: "Обща сума",
            value: currencyFormatter.format(totalValue),
            helper: totalOrders > 0 ? `Средно ${currencyFormatter.format(totalValue / totalOrders)} на документ` : "Няма натрупана стойност",
        },
        {
            label: "Средна честота",
            value: `${averageMonthlyFrequency.toFixed(2)} / месец`,
            helper: averageInterval !== null ? `Среден интервал ${Math.round(averageInterval)} дни` : "Няма достатъчно заявки за интервал",
        },
        {
            label: "Брак от търговеца",
            sublabel: "Брак от рекламации",
            value: traderDefect.available
                ? `${traderDefect.articlePercent.toFixed(1)}% · ${traderDefect.pricePercent.toFixed(1)}%`
                : "—",
            helper: traderDefect.available
                ? `% артикули · % стойност от синхронизирани рекламации. ${defectHelper}`
                : defectHelper,
        },
    ];

    return (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "repeat(5, 1fr)" }, gap: 1.5 }}>
            {metrics.map((metric) => (
                <HistoryMetricCard key={metric.label} {...metric} />
            ))}
        </Box>
    );
}
